import type { FunctionComponent } from "react"
import { useRef, useState } from "react"
import { Cross1Icon } from "@radix-ui/react-icons"
import { Badge } from "./ui/badge"
import { Command, CommandGroup, CommandItem } from "./ui/command"
import { Command as CommandPrimitive } from "cmdk"
import { Separator } from "./ui/separator"
import { Button } from "./ui/button"

export interface DropdownOption {
  value: string
  label: string
  categoryId?: bigint
}

export type FilterOptions = string[] | DropdownOption[]

interface CheckboxFilterProps {
  filterName: string
  options: string[]
  selectedOptions: string[]
  onChange: (selected: string[]) => void
}

interface MultiSelectDropdownFilterProps {
  filterName?: string
  placeholder?: string
  options: DropdownOption[]
  selectedOptions: DropdownOption[]
  onChange: (selected: DropdownOption[]) => void
  className?: string
}

export const CheckboxFilter: FunctionComponent<CheckboxFilterProps> = ({
  filterName,
  options,
  selectedOptions,
  onChange,
}) => {
  const handleCheckboxChange = (option: string) => {
    onChange(
      selectedOptions.includes(option) ?
        selectedOptions.filter((item) => item !== option)
      : [...selectedOptions, option]
    )
  }
  return (
    <div className="my-8">
      <label className="block text-xl font-medium text-foreground my-7">{filterName}</label>
      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-6 my-6">
        {options.map((option) => (
          <div key={option} className="flex items-center">
            <input
              type="checkbox"
              id={option}
              name={option}
              value={option}
              checked={selectedOptions.includes(option)}
              onChange={() => handleCheckboxChange(option)}
              className="h-6 w-6 rounded accent-primary"
            />
            <label htmlFor={option} className="ml-2 block text-sm text-foreground">
              {option}
            </label>
          </div>
        ))}
      </div>
      <Separator className="my-10" />
    </div>
  )
}

export const MultiSelectDropdownFilter: React.FC<MultiSelectDropdownFilterProps> = ({
  filterName,
  placeholder,
  options,
  selectedOptions,
  onChange,
  className,
}) => {
  const [inputValue, setInputValue] = useState("")
  const [open, setOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleUnselect = (option: DropdownOption) => {
    onChange(selectedOptions.filter((s) => s.value !== option.value))
  }

  const handleSelect = (option: DropdownOption) => {
    onChange([...selectedOptions, option])
  }

  const selectables = options.filter(
    (option) => !selectedOptions.some((selectedItem) => selectedItem.value === option.value)
  )

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !inputValue && selectedOptions.length > 0) {
      const lastOption = selectedOptions[selectedOptions.length - 1]
      handleUnselect(lastOption)
    }
  }

  const handleClearAll = () => {
    onChange([])
  }

  return (
    <div className={className}>
      <label className="block text-xl font-medium text-foreground my-7">{filterName}</label>
      <Command className="overflow-visible bg-transparent">
        <div className="group border border-input px-3 py-2 text-sm ring-offset-background rounded-md focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
          <div className="flex gap-1 flex-wrap">
            {selectedOptions.map((option, index) => (
              <Badge key={index}>
                {option.label}
                <button
                  className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  onMouseDown={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                  }}
                  onClick={(e) => {
                    handleUnselect(option)
                    e.preventDefault()
                    e.stopPropagation()
                  }}>
                  <Cross1Icon className="h-3 w-3 ml-1 text-foreground" />
                </button>
              </Badge>
            ))}
            <CommandPrimitive.Input
              ref={inputRef}
              value={inputValue}
              onValueChange={setInputValue}
              onBlur={() => setOpen(false)}
              onFocus={() => setOpen(true)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              className="ml-2 bg-transparent outline-none placeholder:text-muted-foreground flex-1"
            />
          </div>
        </div>
        <div className="relative mt-2">
          {open && selectables.length > 0 ?
            <div className="absolute w-full z-10 top-0 rounded-md border bg-popover text-popover-foreground shadow-md outline-none animate-in max-h-60 overflow-y-auto">
              <CommandGroup>
                {selectables.map((option) => (
                  <CommandItem
                    key={option.value}
                    onMouseDown={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                    }}
                    onSelect={() => {
                      setInputValue("")
                      handleSelect(option)
                    }}
                    className={"cursor-pointer"}>
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </div>
          : null}
        </div>
      </Command>
      <div className="flex justify-between items-center mb-3 mx-1">
        {selectedOptions.length > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-auto p-0 text-xs text-muted-foreground hover:text-foreground"
            onClick={handleClearAll}>
            Clear all
          </Button>
        )}
      </div>
    </div>
  )
}
